import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Icon } from 'assets/icons';
import { Tooltip } from 'app/shared/tooltip';
import { PillToggle } from 'app/shared/pill-toggle';
import { StackedRingsChart } from 'app/shared/charts/stacked-rings';
import { useContactReportService } from 'hooks/users/report';
import styles from './audience-health.module.css';

const TOGGLE_KEY = (org_id) => `gomailer_health_rings_toggle_${org_id}`;

const SEGMENT_META = {
  active: { label: 'Active', color: 'var(--green-primary)' },
  at_risk: { label: 'At risk', color: 'var(--orange-primary)' },
  dormant: { label: 'Dormant', color: 'var(--red-primary)' },
  never_engaged: { label: 'Never engaged', color: 'var(--neutral-dark-4)' }
};

const getScoreDirection = (current_score, comparison_score) => {
  if (comparison_score === null) return null;
  const diff = current_score - comparison_score;
  if (diff > 0) return { label: `+${diff}`, color: 'var(--green-primary)' };
  if (diff < 0) return { label: `${diff}`, color: 'var(--red-primary)' };
  return null;
};

const SegmentRow = ({ segment_key, count, pct }) => {
  const meta = SEGMENT_META[segment_key];
  return (
    <div className={styles.segmentRow}>
      <span className={styles.segmentDot} style={{ backgroundColor: meta.color }} />
      <span className={styles.segmentLabel}>{meta.label}</span>
      <span className={styles.segmentPct}>{pct}%</span>
      <span className={styles.segmentCount}>{count.toLocaleString()}</span>
    </div>
  );
};

const SkeletonState = () => (
  <div className={styles.wrapper}>
    <div className={styles.header}>
      <Skeleton width={130} height={13} />
      <Skeleton width={140} height={13} />
    </div>
    <div className={styles.body}>
      <div className={styles.contentRow}>
        <Skeleton circle width={140} height={140} style={{ flexShrink: 0 }} />
        <div className={styles.rightPanel}>
          <Skeleton width={70} height={12} />
          <Skeleton width={110} height={11} style={{ marginBottom: 6 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={13} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const EmptyState = () => (
  <div className={styles.emptyBody}>
    <Icon name="users" width={22} height={22} className={styles.emptyIcon} />
    <p className={styles.emptyTitle}>No contacts yet</p>
    <p className={styles.emptySubtitle}>
      Import contacts and send your first campaign to see your audience health.
    </p>
  </div>
);

const SuppressedState = () => (
  <div className={styles.emptyBody}>
    <Icon name="campaign" width={22} height={22} className={styles.emptyIcon} />
    <p className={styles.emptyTitle}>Send your first campaign to see your audience health score.</p>
  </div>
);

export const AudienceHealthRings = () => {
  const { tenant_id } = useSelector((store) => store.user_data);
  const { fetchReports } = useContactReportService();

  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggle, setToggle] = useState(() => localStorage.getItem(TOGGLE_KEY(tenant_id)) || 'week');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { reports: data } = await fetchReports({
        query_string: `tenant_id=${tenant_id}&population=30&sort_by=-created_on`
      });
      setSnapshots(data);
    } catch {
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handleVisibility = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [load]);

  const handleToggle = (value) => {
    setToggle(value);
    localStorage.setItem(TOGGLE_KEY(tenant_id), value);
  };

  if (loading) return <SkeletonState />;

  const current = snapshots[0] || null;
  const has_contacts = current && current?.stats?.total_contacts > 0;

  if (!current || !has_contacts) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>Audience health</span>
        </div>
        <EmptyState />
      </div>
    );
  }

  const { stats } = current;
  const has_sent_campaigns =
    stats?.active > 0 || stats?.at_risk > 0 || stats?.dormant > 0 || stats?.never_engaged > 0;

  const scoring_total = stats?.total_contacts - stats?.new_contacts;
  const pct = (v) => (scoring_total > 0 ? Math.round((v / scoring_total) * 100) : 0);

  const day7 = snapshots.find((s) => Date.now() - s.day >= 7 * 24 * 60 * 60 * 1000);
  const day30 = snapshots.find((s) => Date.now() - s.day >= 30 * 24 * 60 * 60 * 1000);
  const comparison_score =
    toggle === 'week' ? day7?.stats?.health_score ?? null : day30?.stats?.health_score ?? null;
  const direction =
    stats?.health_score !== null ? getScoreDirection(stats?.health_score, comparison_score) : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.titleGroup}>
          <span className={styles.title}>Audience health</span>
          <Tooltip content="Score is computed daily from your contact engagement history. New imports are excluded until they receive their first campaign.">
            <button
              className={styles.infoTip}
              type="button"
              aria-label="About the audience health score"
            >
              <Icon name="info" width={13} height={13} />
            </button>
          </Tooltip>
        </span>
        <PillToggle
          options={[
            { value: 'week', label: 'Last week' },
            { value: 'month', label: 'Last month' }
          ]}
          value={toggle}
          onChange={handleToggle}
        />
      </div>

      <div className={styles.body}>
        {!has_sent_campaigns ? (
          <SuppressedState />
        ) : (
          <>
            <div className={styles.contentRow}>
              <StackedRingsChart
                rings={Object.keys(SEGMENT_META).map((key) => ({
                  value: stats[key],
                  total: scoring_total,
                  color: SEGMENT_META[key].color
                }))}
                label={stats?.health_score}
                sublabel="/100"
                size={140}
                className={styles.donutSvg}
              />
              <div className={styles.rightPanel}>
                <div className={styles.rightTop}>
                  {direction && (
                    <span className={styles.direction} style={{ color: direction.color }}>
                      {direction.label}
                    </span>
                  )}
                  <span className={styles.scoreSub}>
                    {scoring_total.toLocaleString()} contacts scored
                  </span>
                </div>
                <div className={styles.segments}>
                  {Object.keys(SEGMENT_META).map((key) => (
                    <SegmentRow
                      key={key}
                      segment_key={key}
                      count={stats[key]}
                      pct={pct(stats[key])}
                    />
                  ))}
                </div>
              </div>
            </div>

            {stats?.new_contacts > 0 && (
              <>
                <div className={styles.divider} />
                <div className={styles.newContactsRow}>
                  <Tooltip content="Contacts imported after your last campaign send. They're excluded from the health score until they've received at least one campaign and had a chance to engage.">
                    <span className={styles.newContactsLabel}>
                      Pending first send
                      <span className={styles.infoTip}>
                        <Icon name="info" width={12} height={12} />
                      </span>
                    </span>
                  </Tooltip>
                  <span className={styles.newContactsCount}>
                    {stats?.new_contacts.toLocaleString()} contacts
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
